import { useLanguage } from "@/i18n/LanguageContext";
import { languageNames, type Language } from "@/i18n/translations";
import { Globe } from "lucide-react";
import { useState, useRef, useEffect } from "react";

const languages: Language[] = ["en", "ar", "ru"];
const languageCodes: Record<Language, string> = { en: "EN", ar: "AR", ru: "RU" };

interface LanguageSwitcherProps {
  compact?: boolean;
}

const LanguageSwitcher = ({ compact = false }: LanguageSwitcherProps) => {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative z-50" style={{ direction: "ltr" }}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border bg-card/80 backdrop-blur-sm text-xs font-display text-foreground hover:border-gold/30 transition-colors"
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5 text-gold" />
        <span>{compact ? languageCodes[language] : languageNames[language]}</span>
      </button>
      {open && (
        <div className="absolute top-full mt-1 right-0 rounded-lg border border-border bg-card shadow-xl overflow-hidden min-w-[120px]">
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => { setLanguage(lang); setOpen(false); }}
              className={`w-full px-4 py-2 text-sm font-display text-left hover:bg-secondary transition-colors ${
                lang === language ? "text-gold bg-secondary/50" : "text-foreground"
              }`}
            >
              {languageNames[lang]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
